import { useState, useRef, useEffect, useCallback } from 'react'
import { LABEL_COLORS } from '@/constants/LabelColors'
import type { PlaybookLabel } from '@/types/Label.types'
import './playbook-labels-panel.css'

interface PlaybookLabelsPanelProps {
	labels: PlaybookLabel[]
	usageCounts: Array<{
		label_id: string
		play_count: number
		formation_count: number
		concept_count: number
	}>
	onUpdateLabel: (data: { id: string; name?: string; color?: string }) => Promise<void>
	onDeleteLabel: (id: string) => Promise<void>
	onReorder: (labelIds: string[]) => Promise<void>
	onCreateLabel: (data: { name: string; color: string }) => Promise<void>
}

export function PlaybookLabelsPanel({
	labels,
	usageCounts,
	onUpdateLabel,
	onDeleteLabel,
	onReorder,
	onCreateLabel,
}: PlaybookLabelsPanelProps) {
	const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
	const [editingNames, setEditingNames] = useState<Record<string, string>>({})
	const [openColorPicker, setOpenColorPicker] = useState<string | null>(null)
	const colorPickerRef = useRef<HTMLDivElement>(null)

	const getUsage = (labelId: string) => {
		const c = usageCounts.find((u) => u.label_id === labelId)
		return c ?? { play_count: 0, formation_count: 0, concept_count: 0 }
	}

	const getTotalUsage = (labelId: string) => {
		const c = getUsage(labelId)
		return c.play_count + c.formation_count + c.concept_count
	}

	const handleNameChange = (labelId: string, name: string) => {
		setEditingNames((prev) => ({ ...prev, [labelId]: name }))
	}

	const handleNameBlur = async (label: PlaybookLabel) => {
		const newName = editingNames[label.id]
		if (newName !== undefined && newName !== label.name && newName.trim()) {
			await onUpdateLabel({ id: label.id, name: newName.trim() })
		}
		setEditingNames((prev) => {
			const next = { ...prev }
			delete next[label.id]
			return next
		})
	}

	const handleColorChange = async (labelId: string, color: string) => {
		await onUpdateLabel({ id: labelId, color })
		setOpenColorPicker(null)
	}

	const handleDelete = async (labelId: string) => {
		await onDeleteLabel(labelId)
		setConfirmDelete(null)
	}

	const handleCloseColorPicker = useCallback(() => setOpenColorPicker(null), [])

	useEffect(() => {
		if (!openColorPicker) return
		const handleClickOutside = (e: MouseEvent) => {
			if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
				handleCloseColorPicker()
			}
		}
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') handleCloseColorPicker()
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleKeyDown)
		}
	}, [openColorPicker, handleCloseColorPicker])

	if (labels.length === 0) {
		return (
			<div className="playbook-labels-empty">
				<p>No labels yet. Labels help organize plays, formations, and concepts across your playbook.</p>
			</div>
		)
	}

	return (
		<div className="playbook-labels-panel">
			<div className="playbook-labels-list">
				{labels.map((label) => {
					const usage = getUsage(label.id)
					const total = getTotalUsage(label.id)

					return (
						<div key={label.id} className="playbook-label-row">
							{/* Drag handle */}
							<span className="playbook-label-drag" aria-hidden="true">&#x2807;</span>

							{/* Color swatch */}
							<div
								className="playbook-label-color-picker"
								data-open={openColorPicker === label.id}
								ref={openColorPicker === label.id ? colorPickerRef : undefined}
							>
								<button
									className="playbook-label-swatch"
									style={{ backgroundColor: label.color }}
									onClick={() => setOpenColorPicker(openColorPicker === label.id ? null : label.id)}
									aria-label={`Change color for ${label.name}`}
									aria-expanded={openColorPicker === label.id}
								/>
								<div className="playbook-label-color-dropdown" role="radiogroup" aria-label="Label color">
									{LABEL_COLORS.map((c) => (
										<button
											key={c}
											className="label-color-swatch"
											data-selected={label.color === c}
											style={{ backgroundColor: c }}
											onClick={() => handleColorChange(label.id, c)}
											role="radio"
											aria-checked={label.color === c}
											aria-label={`Color ${c}`}
										/>
									))}
								</div>
							</div>

							{/* Editable name */}
							<input
								className="playbook-label-name"
								value={editingNames[label.id] ?? label.name}
								onChange={(e) => handleNameChange(label.id, e.target.value)}
								onBlur={() => handleNameBlur(label)}
								maxLength={100}
								aria-label={`Label name: ${label.name}`}
							/>

							{/* Usage counts */}
							<span className="playbook-label-usage">
								{usage.play_count > 0 && <span>{usage.play_count} plays</span>}
								{usage.formation_count > 0 && <span>{usage.formation_count} formations</span>}
								{usage.concept_count > 0 && <span>{usage.concept_count} concepts</span>}
								{total === 0 && <span className="playbook-label-unused">Unused</span>}
							</span>

							{/* Delete */}
							{confirmDelete === label.id ? (
								<div className="playbook-label-confirm">
									<span>
										{total > 0
											? `Remove this label from ${total} items?`
											: 'Delete this label?'}
									</span>
									<button className="playbook-label-confirm-delete" onClick={() => handleDelete(label.id)}>Delete</button>
									<button className="playbook-label-confirm-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
								</div>
							) : (
								<button
									className="playbook-label-delete"
									onClick={() => setConfirmDelete(label.id)}
									aria-label={`Delete ${label.name}`}
								>
									&times;
								</button>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
